import React from 'react';
import styled from '@emotion/styled';
// --- external imports ------

import { PointerCursor, DeniedCursor } from 'Cursors';
import Asset from './BaseAssets';
// --- local imports ------

/**
 * A JSX component to render button with controlled hover area
 * used for button with skewed projections
 * [bug 1083](https://mirqah.kanbanize.com/ctrl_board/8/cards/1083/details/)
 * 
 * Hover area is defined using SVG path. 
 * To generate SVG you can use [Method.ac editor](https://editor.method.ac/)
 * 	- import image in editor
 *  - use path tool to draw path
 * 	- save as SVG
 * 	- Open SVG and extract path from exported SVG definition
 * 
 * ButtonWithCustomTooltip expecting only one child its properties are 
 * used for ButtonWithCustomTooltip.Tooltip properties
 * if more than one child defined error will be thrown
 * 
 * @param {Object} props - all properties are passed to Asset.Button expects 
 * 	hoverPath, onClick and children.
 * @param {string} props.hoverPath - a String represents hover area in SVG path format
 * @param {string} props.className - CSS Class
 *
 * @returns {JSX.Element}
 */
function ButtonWithCustomTooltip(props) {
	const [isTooltipDisplayed, setIsTooltipDisplayed] = React.useState(false);
	const {
		hoverPath,
		onClick: handleOnClick,
		children,
		isDisabled,
		'data-testid': dataTestId,
		...buttonProps
	} = props;
	const ToolTip = React.Children.only(children);
	const scale = buttonProps.scale ?? 1;

	React.useEffect(() => {
		// To fix tooltip is showing after button is hidden e.g. calculator
		setIsTooltipDisplayed(false);
	}, [props.isHidden]);

	if (props.isHidden) {
		return null;
	}

	function handleOnMouseEnter() {
		props?.setIsGlowing(true);
		setIsTooltipDisplayed(true);
	}

	function handleOnMouseLeave() {
		props?.setIsGlowing(false);
		setIsTooltipDisplayed(false);
	}

	return (
		<>
			<Asset.Button
				{...buttonProps}
			/>

			<Asset.Svg
				x={buttonProps.x}
				y={buttonProps.y}
				projection={{
					width: buttonProps.projection.width * scale,
					height: buttonProps.projection.height * scale,
				}}
				/* viewBox is used to keep SVG responsive during window size change */
				viewBox={`0 0 ${buttonProps.projection.width} ${buttonProps.projection.height}`}
				style={{
					zIndex: 1,
				}}
			>
				<path
					xmlns="http://www.w3.org/2000/svg"
					fill={`${props.debug ? 'red' : 'transparent'}`}
					stroke="none"
					pointerEvents="all"
					d={hoverPath}
					onMouseEnter={handleOnMouseEnter}
					onMouseLeave={handleOnMouseLeave}
					onClick={handleOnClick}
					css={isDisabled ? DeniedCursor : PointerCursor}
					data-testid={dataTestId}
				/>
			</Asset.Svg>

			<ButtonWithCustomTooltip.Tooltip
				{...ToolTip.props}
				isDisplayed={isTooltipDisplayed}
			/>
		</>
	);
};

ButtonWithCustomTooltip.Tooltip = styled(Asset.Img)`
	display: ${({ isDisplayed }) => isDisplayed ? 'block' : 'none'};
`;


export default ButtonWithCustomTooltip;
